import React, { useEffect, useRef, useState } from "react"
import { FormFeedback, Input, Label } from "reactstrap"
import { emptyOtherAddress } from "../../helpers/registerOtherAddress"
import {
  formatOtherAddressLabel,
  hasGooglePlacesApiKey,
  loadGooglePlaces,
  parsePlaceAddress,
} from "../../helpers/googlePlaces"

/**
 * Single-field city picker powered by Google Places Autocomplete.
 * Becomes active when REACT_APP_GOOGLE_PLACES_API_KEY is set (restart required).
 */
const PlacesCityInput = ({
  label = "City",
  required = false,
  value,
  onChange,
  onBlur,
  invalid = false,
  error = "",
  disabled = false,
  placeholder = "Search city, state, country…",
  id,
}) => {
  const inputRef = useRef(null)
  const autocompleteRef = useRef(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const [status, setStatus] = useState(() =>
    hasGooglePlacesApiKey() ? "loading" : "missing_key",
  )

  const display = formatOtherAddressLabel(value)

  useEffect(() => {
    if (!hasGooglePlacesApiKey()) {
      setStatus("missing_key")
      return undefined
    }

    let cancelled = false
    setStatus("loading")

    loadGooglePlaces()
      .then(google => {
        if (cancelled || !inputRef.current) return

        if (autocompleteRef.current) {
          google.maps.event.clearInstanceListeners(autocompleteRef.current)
        }

        const autocomplete = new google.maps.places.Autocomplete(
          inputRef.current,
          {
            types: ["(cities)"],
            fields: ["address_components", "formatted_address", "name"],
          },
        )
        autocompleteRef.current = autocomplete

        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace()
          const parsed = parsePlaceAddress(place)
          onChangeRef.current?.(parsed)
          if (inputRef.current) {
            inputRef.current.value = formatOtherAddressLabel(parsed)
          }
        })

        setStatus("ready")
      })
      .catch(error => {
        if (cancelled) return
        setStatus(
          error?.message === "missing_key" ? "missing_key" : "error",
        )
      })

    return () => {
      cancelled = true
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(
          autocompleteRef.current,
        )
      }
      autocompleteRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!inputRef.current) return
    if (document.activeElement === inputRef.current) return
    if (inputRef.current.value !== display) {
      inputRef.current.value = display
    }
  }, [display])

  return (
    <div className="auth-page__other-address">
      <div className="auth-page__field">
        {label ? (
          <Label className={`form-label${required ? " isRequired" : ""}`} for={id}>
            {label}
          </Label>
        ) : null}
        <Input
          id={id}
          innerRef={inputRef}
          type="text"
          autoComplete="off"
          className="form-control"
          placeholder={
            status === "loading"
              ? "Loading city search…"
              : status === "error"
                ? "City search unavailable"
                : placeholder
          }
          defaultValue={display}
          disabled={disabled || status !== "ready"}
          invalid={invalid}
          onChange={() => {
            // Typing without picking a suggestion clears structured value.
            onChangeRef.current?.(emptyOtherAddress())
          }}
          onBlur={onBlur}
        />
        {invalid && error ? (
          <FormFeedback type="invalid">{error}</FormFeedback>
        ) : null}
        {status === "error" ? (
          <p className="auth-page__other-address-hint">
            Could not load Google Places. Check the API key and that Places API
            is enabled.
          </p>
        ) : null}
        {status === "ready" ? (
          <p className="auth-page__other-address-hint">
            Start typing and select a city from the suggestions.
          </p>
        ) : null}
      </div>
    </div>
  )
}

export default PlacesCityInput
